import { DateTime } from "luxon";
import { z } from "zod/v4-mini";

import { TandemConstants } from "@/lib/drivers/tandem/TandemConstants";
import { TandemCommand, TandemResponse } from "@/lib/drivers/tandem/TandemTypes";
import { parseEvent } from "@/lib/drivers/tandem/TandemUtils";
import { cz } from "@/utils/zodUtils";

export type EventIssue =
  | "RETRY" // Request should be retried
  | "UNKNOWN" // Unknown issue
  | "NONE"; // No issue

export type EventPayload = {
  index: number;
  header_id: number;
  header_ts: number;
  header_log_seq_no: number;
  header_spare: number;
  tdeps: Uint8Array;
};

export type ValidTandemEvent<T> = {
  index: number;
  id: number;
  timestamp: DateTime;
  eventSequenceNum: number;
  spare: number;
  tdeps: Uint8Array;

  name: string;
  payload: T;
  valid: true;
};

export type InvalidTandemEvent = {
  index: number;
  id: number;
  timestamp: DateTime;
  eventSequenceNum: number;
  spare: number;
  tdeps: Uint8Array;

  name: undefined;
  payload: undefined;
  valid: false;
};

export type TandemEvent<T> = ValidTandemEvent<T> | InvalidTandemEvent;

export type EventList = {
  events: TandemEvent<unknown>[];
  requestedCount: number;
  receivedCount: number;
  ignoredCount: number;
  missedCount: number;
  totalCount: number;
  issue: EventIssue;
};

export const emptyEventList = (requestedCount?: number, issue?: EventIssue): EventList => ({
  events: [],
  requestedCount: requestedCount ?? 0,
  receivedCount: 0,
  ignoredCount: 0,
  missedCount: 0,
  totalCount: 0,
  issue: issue ?? "NONE",
});

export class TandemEventError extends Error {
  type: EventIssue;

  constructor(issue: EventIssue) {
    super(`Event Error: ${issue}`);
    this.type = issue;
  }
}

export const eventErrorSchema = z.object({
  type: z.literal(["RETRY", "UNKNOWN", "NONE"]),
});

const eventPayloadSchema = z.object({
  index: z.number(),
  header_id: z.number(),
  header_ts: z.number(),
  header_log_seq_no: z.number(),
  header_spare: z.number(),
  tdeps: cz.unsafe<Uint8Array>(),
});

export const EVENT_RESPONSE = {
  value: 125,
  name: "Event History Log Entry",
  version: 36102,
  format: "i2.hiii16B",
  fields: ["index", "header_id", "header_ts", "header_log_seq_no", "header_spare", "tdeps"] as const,
  postprocess: rec => {
    const event = parseEvent(rec.header_id, rec.tdeps);

    const eventData = {
      index: rec.index,
      id: rec.header_id,
      timestamp: DateTime.fromSeconds(TandemConstants.BASE_TIME + rec.header_ts),
      eventSequenceNum: rec.header_log_seq_no,
      spare: rec.header_spare,
      tdeps: rec.tdeps,
    };

    if (event?.payload) {
      return {
        ...eventData,
        name: event.name,
        payload: event.payload,
        valid: true,
      };
    }

    return {
      ...eventData,
      name: undefined,
      payload: undefined,
      valid: false,
    };
  },
  schema: eventPayloadSchema,
} as const satisfies TandemResponse<EventPayload, TandemEvent<unknown>>;

export const MULTI_EVENT_COMMAND = {
  value: 152,
  name: "Multiple Event History Log Request by Sequence",
  version: 59814,
  format: "ii",
  fields: ["seqNum", "count"],
  response: EVENT_RESPONSE,
} as const satisfies TandemCommand<EventPayload, TandemEvent<unknown>>;

export const EVENT_COMMAND = {
  value: 151,
  name: "Event History Log Request By Sequence",
  version: 36102,
  format: "i",
  fields: ["seqNum"],
  response: EVENT_RESPONSE,
} as const satisfies TandemCommand<EventPayload, TandemEvent<unknown>>;

export const STOP_MULTI_EVENT_COMMAND = {
  value: 153,
  name: "Stops Multiple Event History Log Download",
  version: 47144,
} as const satisfies TandemCommand;
